import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import ReplayIcon from '@material-ui/icons/Replay';
import CheckIcon from '@material-ui/icons/Check';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSButton } from '../../../shared/components/buttons';

const AttachmentCameraCapture = ({ onCapture, disabled = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera capture isn't supported in this browser. Use Attach File instead.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setError('Camera access was denied. Allow camera permission, or use Attach File instead.');
      } else if (err?.name === 'NotFoundError') {
        setError('No camera was found on this device. Use Attach File instead.');
      } else {
        setError("Couldn't access the camera. Use Attach File instead.");
      }
    }
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setPreviewUrl(null);
    setCapturedBlob(null);
    startCamera();
  }, [startCamera]);

  const handleClose = useCallback(() => {
    stopStream();
    setOpen(false);
    setError(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setCapturedBlob(null);
  }, [stopStream]);

  const handleSnap = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    stopStream();

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setCapturedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.92
    );
  }, [stopStream]);

  const handleRetake = useCallback(() => {
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setCapturedBlob(null);
    startCamera();
  }, [startCamera]);

  const handleUsePhoto = useCallback(() => {
    if (!capturedBlob) return;

    const file = new File([capturedBlob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture?.(file);
    handleClose();
  }, [capturedBlob, onCapture, handleClose]);

  const renderActions = useCallback(() => {
    if (error) {
      return <RMSButton label="Close" variant="outlined" onClick={handleClose} />;
    }

    if (previewUrl) {
      return (
        <>
          <RMSButton
            label="Retake"
            variant="outlined"
            prefixIcon={<ReplayIcon />}
            onClick={handleRetake}
          />
          <RMSButton label="Use Photo" prefixIcon={<CheckIcon />} onClick={handleUsePhoto} />
        </>
      );
    }

    return <RMSButton label="Capture" prefixIcon={<CameraAltIcon />} onClick={handleSnap} />;
  }, [error, previewUrl, handleClose, handleRetake, handleUsePhoto, handleSnap]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopStream();
    };
  }, [stopStream]);

  return (
    <>
      <RMSButton
        label="Camera"
        variant="outlined"
        prefixIcon={<CameraAltIcon />}
        disabled={disabled}
        onClick={handleOpen}
      />

      {open && (
        <RMSCommonDialog
          open={open}
          title={previewUrl ? 'Review Photo' : 'Take Photo'}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          closeOnBackdropClick={false}
          saveButton={{ show: false }}
          cancelButton={{ show: false }}
          customButtons={renderActions()}>
          {error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box className="attachment-camera-capture">
              <video
                ref={videoRef}
                className={
                  previewUrl
                    ? 'attachment-camera-capture__video attachment-camera-capture__video--hidden'
                    : 'attachment-camera-capture__video'
                }
                autoPlay
                muted
                playsInline
              />

              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Captured preview"
                  className="attachment-camera-capture__preview"
                />
              )}
            </Box>
          )}

          <canvas ref={canvasRef} className="attachment-camera-capture__hidden-canvas" />
        </RMSCommonDialog>
      )}
    </>
  );
};

export default AttachmentCameraCapture;
